/**
 * newCopy entries for the SENT_ITEM family plus ASSIGNEE_BOUNCE (a separate
 * family) — a bounced email reads the same to the reader regardless of
 * which code path recorded it, so the two share copy.
 */
import { ChannelType, SupportingActorType } from '@dadohr/shared/domainTypes/experience';
import React, { ReactNode } from 'react';
import { CopyPack, Issue, IssueCopyContext, IssueKind } from '../types';
import {
  ctxOf,
  dataOfFamily,
  emailFieldLabel,
  genericRetrySupportSteps,
  HELP_ARTICLES,
  HelpArticle,
  importOrEditProfileSteps,
  personLabel,
  retryStep,
  SupportEmail,
  tt
} from './shared';

const dataOf = dataOfFamily('SENT_ITEM');
const assigneeBounceDataOf = dataOfFamily('ASSIGNEE_BOUNCE');

/** "work email" / "personal email" per the channel the message was sent
 * from; plain "email" for records that didn't store the channel. */
const postmarkEmailLabel = (g: Issue[]): string => {
  const c = ctxOf(g);
  const emailFrom = dataOf(g).message?.emailFrom;
  return emailFrom ? emailFieldLabel(emailFrom) : c.t('email');
};

/** Slack and Teams look the recipient up by their Dado work email. The
 * backend sends anyway when it is blank and records the lookup failure, so
 * the blank address on the message record is the only sign the person has
 * no work email rather than no Slack/Teams account. */
const hasNoRecipientEmail = (g: Issue[]): boolean => !dataOf(g).message?.recipientEmail;

const noEmail__howToFixIt = (ctx: IssueCopyContext, label: string): ReactNode => (
  <ul>
    <li>{tt(ctx, 'Click retry to see if their {{ addressType }} has since been added to Dado', { addressType: label })}</li>
    <li>
      {tt(ctx, 'If this does not work, add their {{ addressType }} by either:', { addressType: label })}
      <ul>{importOrEditProfileSteps(label)}</ul>
    </li>
    {retryStep()}
  </ul>
);

const bounced__summary = (ctx: IssueCopyContext, fullName: string, emailAddress: string) =>
  tt(ctx, `Email(s) couldn't be sent to {{ fullName }}: {{ emailAddress }} bounced`, { fullName, emailAddress });

const bounced__whatCausedIt = (ctx: IssueCopyContext, emailAddress: string): ReactNode => (
  <>
    {tt(ctx, 'When Dado tried to send an email to {{ emailAddress }}, it bounced.', { emailAddress })}{' '}
    {tt(
      ctx,
      'This can happen if the email address is incorrect; the account is inactive; the inbox is full; or the address is not configured to accept email from external senders.'
    )}
  </>
);

const bounced__howToFixIt = (ctx: IssueCopyContext): ReactNode => (
  <ul>
    <li>{tt(ctx, 'Click retry to see if the issues causing the bounce have already been resolved')}</li>
    <li>
      {tt(ctx, 'If this does not work, confirm that this email is correct. If you need to update the email, you can do so by either:')}
      <ul>{importOrEditProfileSteps()}</ul>
    </li>
    <li>
      {tt(
        ctx,
        'If this is a work email address, also check that this account is active and is configured to accept email from senders outside of your organization.'
      )}
    </li>
    {retryStep()}
  </ul>
);

export const sentItemNewCopy = {
  [IssueKind.ASSIGNEE_BOUNCE]: {
    category: 'Email bounced',
    summary: (g) => {
      const d = assigneeBounceDataOf(g);
      return bounced__summary(ctxOf(g), d.displayName, d.email);
    },
    whatCausedIt: (g) => bounced__whatCausedIt(ctxOf(g), assigneeBounceDataOf(g).email),
    howToFixIt: (g) => bounced__howToFixIt(ctxOf(g))
  },
  [IssueKind.SENT_ITEM__POSTMARK_BOUNCED]: {
    category: 'Email bounced',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return bounced__summary(c, personLabel(c, d.actor, d.instance), d.message.recipientEmail);
    },
    whatCausedIt: (g) => bounced__whatCausedIt(ctxOf(g), dataOf(g).message.recipientEmail),
    howToFixIt: (g) => bounced__howToFixIt(ctxOf(g))
  },
  [IssueKind.SENT_ITEM__POSTMARK_INACTIVE_RECIPIENT]: {
    category: 'Email bounced',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return bounced__summary(c, personLabel(c, d.actor, d.instance), d.message.recipientEmail);
    },
    whatCausedIt: (g) => bounced__whatCausedIt(ctxOf(g), dataOf(g).message.recipientEmail),
    howToFixIt: (g) => bounced__howToFixIt(ctxOf(g))
  },
  [IssueKind.SENT_ITEM__POSTMARK_NO_EMAIL]: {
    category: 'Email undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      return tt(c, 'Email(s) couldn’t be sent to {{ fullName }}: no {{ addressType }} in Dado', {
        fullName,
        addressType: postmarkEmailLabel(g)
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When Dado tried to send an email to {{ fullName }}, they didn’t have a {{ addressType }} address in Dado.', {
            fullName,
            addressType: postmarkEmailLabel(g)
          })}
        </>
      );
    },
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), postmarkEmailLabel(g))
  },
  [IssueKind.SENT_ITEM__POSTMARK_DEFAULT]: {
    category: 'Email undeliverable',
    summary: (g) => tt(ctxOf(g), "Email(s) couldn't be sent to {{ fullName }}: unknown issue", {
      fullName: personLabel(ctxOf(g), dataOf(g).actor, dataOf(g).instance)
    }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      const fullName = personLabel(c, d.actor, d.instance);
      return (
        <>
          {tt(
            c,
            'When Dado tried to send an email to {{ fullName }} ({{ emailAddress }}), an unknown issue occurred and the email could not be delivered.',
            { fullName, emailAddress: d.message.recipientEmail }
          )}
        </>
      );
    },
    howToFixIt: () => genericRetrySupportSteps()
  },

  [IssueKind.SENT_ITEM__SLACK_CHANNEL_ACCESS]: {
    category: 'Slack message undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      const supportingActor = c.supportingActors?.[d.actor];
      const channelName =
        supportingActor &&
        supportingActor.type === SupportingActorType.CHANNEL &&
        supportingActor.channeltype === ChannelType.SLACK
          ? supportingActor.channelName
          : undefined;
      return tt(c, `Slack message(s) to "{{ channelName }}" couldn't be sent: channel not found`, {
        channelName: channelName ? `#${channelName}` : ''
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      const supportingActor = c.supportingActors?.[d.actor];
      const channelName =
        supportingActor &&
        supportingActor.type === SupportingActorType.CHANNEL &&
        supportingActor.channeltype === ChannelType.SLACK
          ? supportingActor.channelName
          : undefined;
      const label = channelName ? `#${channelName}` : '';
      return (
        <>
          {tt(c, 'When Dado tried to send a Slack message to')} <strong>{label}</strong>{' '}
          {tt(
            c,
            "it couldn't find that channel. Usually this means the channel was deleted or the Dado app is not present in that channel."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      const supportingActor = c.supportingActors?.[d.actor];
      const channelName =
        supportingActor &&
        supportingActor.type === SupportingActorType.CHANNEL &&
        supportingActor.channeltype === ChannelType.SLACK
          ? supportingActor.channelName
          : undefined;
      const label = channelName ? `#${channelName}` : '';
      return (
        <ul>
          <li>
            {tt(c, 'Check the')} <strong>{label}</strong> {tt(c, 'channel still exists')}
          </li>
          <li>
            {tt(c, 'If not, contact')} <SupportEmail />{' '}
            {tt(c, 'to ask for the Supporting Actor to be updated to a new channel')}
          </li>
          <li>
            {tt(c, 'Confirm the Dado app has been added to the')} <strong>{label}</strong> {tt(c, 'channel')}{' '}
            <HelpArticle href={HELP_ARTICLES.slackGrantBotAccess} />
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SENT_ITEM__SLACK_DEFAULT]: {
    category: 'Slack message undeliverable',
    summary: (g) => tt(ctxOf(g), "Slack message(s) to {{ fullName }} couldn't be sent", {
      fullName: personLabel(ctxOf(g), dataOf(g).actor, dataOf(g).instance)
    }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When Dado tried to send a Slack message to {{ fullName }}, Slack rejected the message.', { fullName })}{' '}
          {tt(c, 'This can be caused by issues with the content of the notification or issues connecting to the Slack API.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Wait a few minutes and click retry to see if the underlying issues have resolved')}</li>
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(
              c,
              "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
            )}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.SENT_ITEM__SLACK_USERS_NOT_FOUND]: {
    category: 'Slack message undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      if (hasNoRecipientEmail(g))
        return tt(c, "Slack message(s) to {{ fullName }} couldn't be sent: no work email in Dado", { fullName });
      return tt(c, "Slack message(s) to {{ fullName }} couldn't be sent: couldn't find their Slack account", { fullName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      if (hasNoRecipientEmail(g))
        return (
          <>
            {tt(c, 'When Dado tried to send a Slack message to {{ fullName }}, they didn’t have a work email address in Dado.', {
              fullName
            })}{' '}
            {tt(c, "Dado uses work emails to look up someone's Slack account.")}
          </>
        );
      return (
        <>
          {tt(c, "When Dado tried to send a Slack message to {{ fullName }}, it couldn't find their Slack account.", {
            fullName
          })}{' '}
          {tt(
            c,
            "This can happen if their Slack account is not active, they are not in the relevant Slack Workspace, or their work email address in Dado doesn't match their Slack account."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      if (hasNoRecipientEmail(g)) return noEmail__howToFixIt(c, 'work email');
      const emailAddress = dataOf(g).message.recipientEmail;
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if they now have an active Slack account in this workspace')}</li>
          <li>
            {tt(c, 'If this does not work, confirm their email address in Slack matches what’s in Dado: {{ emailAddress }}. If you need to update the email in Dado, you can do so by either:', {
              emailAddress
            })}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },

  [IssueKind.SENT_ITEM__TEAMS_NO_ERROR]: {
    category: 'Teams message undeliverable',
    // Deliberately distinct from legacy's fallback for this case, which is an
    // accidental untranslated 'Unknown Error' literal, not a considered string.
    summary: (g) => tt(ctxOf(g), 'Teams message(s) couldn’t be sent: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When Dado tried to send this Teams message, Dado had issues connecting to the Microsoft API. This is usually a passing issue, but can also be caused by problems with the Dado<>Microsoft integration.'
      ),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.SENT_ITEM__TEAMS_OTHER]: {
    category: 'Teams message undeliverable',
    summary: (g) => tt(ctxOf(g), "Teams message(s) couldn't be sent to {{ fullName }}: unknown issue", {
      fullName: personLabel(ctxOf(g), dataOf(g).actor, dataOf(g).instance)
    }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When Dado tried to send a Teams notification to {{ fullName }}, an unknown issue occurred.', {
            fullName
          })}{' '}
          {tt(c, 'This can sometimes be caused by passing issues with the Microsoft API, or {{ fullName }} not having access to the Dado Teams app.', {
            fullName
          })}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const emailAddress = dataOf(g).message.recipientEmail;
      return (
        <ul>
          <li>{tt(c, 'Wait a few minutes and click retry')}</li>
          <li>
            {tt(c, 'If this does not work, confirm their Microsoft email address matches what’s in Dado: {{ emailAddress }}. If you need to update the email in Dado, you can do so by either:', {
              emailAddress
            })}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SENT_ITEM__TEAMS_USER_NOT_FOUND]: {
    category: 'Teams message undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      if (hasNoRecipientEmail(g))
        return tt(c, "Teams message(s) couldn't be sent to {{ fullName }}: no work email in Dado", { fullName });
      return tt(c, "Teams message(s) couldn't be sent to {{ fullName }}: couldn't find their Teams account", { fullName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      if (hasNoRecipientEmail(g))
        return (
          <>
            {tt(c, 'When Dado tried to send a Teams message to {{ fullName }}, they didn’t have a work email address in Dado.', {
              fullName
            })}{' '}
            {tt(c, "Dado uses work emails to look up someone's Teams account.")}
          </>
        );
      return (
        <>
          {tt(c, "When Dado tried to send a Teams notification to {{ fullName }}, it could not find their Teams account.", {
            fullName
          })}{' '}
          {tt(
            c,
            "This can sometimes happen if their work email address in Dado doesn't match their Microsoft email account, or they have not been given access to the Dado Teams app."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      if (hasNoRecipientEmail(g)) return noEmail__howToFixIt(c, 'work email');
      const d = dataOf(g);
      const fullName = personLabel(c, d.actor, d.instance);
      return (
        <ul>
          <li>{tt(c, 'Wait a few minutes and click retry')}</li>
          <li>
            {tt(c, 'If this does not work, confirm their Microsoft email address matches what’s in Dado: {{ emailAddress }}. If you need to update the email in Dado, you can do so by either:', {
              emailAddress: d.message.recipientEmail
            })}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          <li>
            {tt(c, 'Check that {{ fullName }} has been given access to the Dado Teams app by your IT team. If not request that they be given access.', {
              fullName
            })}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },

  [IssueKind.SENT_ITEM__TWILIO_NO_PHONE_NUMBER]: {
    category: 'SMS undeliverable',
    summary: (g) => tt(ctxOf(g), 'SMS couldn’t be sent to {{ fullName }}: no phone number in Dado', {
      fullName: personLabel(ctxOf(g), dataOf(g).actor, dataOf(g).instance)
    }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      return <>{tt(c, 'When Dado tried to send an SMS, {{ fullName }} didn’t have a phone number in Dado.', { fullName })}</>;
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actor, dataOf(g).instance);
      return (
        <ul>
          <li>
            {tt(c, 'Add a phone number to Dado for {{ fullName }} by either:', { fullName })}
            <ul>{importOrEditProfileSteps('phone number')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SENT_ITEM__TWILIO_UNREACHABLE]: {
    category: 'SMS undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return tt(c, 'SMS couldn’t be sent to {{ fullName }}: phone number {{ phoneNumber }} unreachable', {
        fullName: personLabel(c, d.actor, d.instance),
        phoneNumber: d.message.to
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      const fullName = personLabel(c, d.actor, d.instance);
      return (
        <>
          {tt(
            c,
            'When Dado tried to send an SMS to {{ fullName }}’s phone number ({{ phoneNumber }}), the phone was switched off, out of coverage or otherwise unavailable.',
            { fullName, phoneNumber: d.message.to }
          )}{' '}
          {tt(c, 'Sometimes this means the phone number in Dado is not correct.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'If this task or message is also sent via email, you could choose to ignore this error.')}</li>
          <li>
            {tt(c, 'If this must be sent via SMS, confirm the phone number is correct in Dado. If you need to update it, you can do so by either:')}
            <ul>{importOrEditProfileSteps('phone number')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SENT_ITEM__TWILIO_INVALID]: {
    category: 'SMS undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return tt(c, 'SMS couldn’t be sent to {{ fullName }}: phone number {{ phoneNumber }} invalid or no longer exists', {
        fullName: personLabel(c, d.actor, d.instance),
        phoneNumber: d.message.to
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      const fullName = personLabel(c, d.actor, d.instance);
      return (
        <>
          {tt(
            c,
            'When Dado tried to send an SMS to {{ fullName }}’s phone number ({{ phoneNumber }}), the network reported it as invalid or no longer existing.',
            { fullName, phoneNumber: d.message.to }
          )}{' '}
          {tt(c, 'This usually means the phone number in Dado has a typo.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'If this task or message is also sent via email, you could choose to ignore this error.')}</li>
          <li>
            {tt(c, 'If this must be sent via SMS, update the phone number in Dado by either:')}
            <ul>{importOrEditProfileSteps('phone number')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SENT_ITEM__TWILIO_OTHER]: {
    category: 'SMS undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return tt(c, 'SMS couldn’t be sent to {{ fullName }}: unknown issue', { fullName: personLabel(c, d.actor, d.instance) });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      const fullName = personLabel(c, d.actor, d.instance);
      return (
        <>
          {tt(c, 'When Dado tried to send an SMS to {{ fullName }}’s phone number ({{ phoneNumber }}), an unknown issue occurred.', {
            fullName,
            phoneNumber: d.message.to
          })}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'If this task or message is also sent via email, you could choose to ignore this error.')}</li>
          <li>{tt(c, 'If this must be sent via SMS, wait a few minutes and click retry')}</li>
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(
              c,
              "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
            )}
          </li>
        </ul>
      );
    }
  },

  [IssueKind.SENT_ITEM__UNKNOWN]: {
    category: 'Unknown error',
    summary: (g) => tt(ctxOf(g), 'An unknown error has occurred'),
    whatCausedIt: (g) => tt(ctxOf(g), 'An uncommon and unexpected issue has occurred with the experiences listed below.'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <>
          {tt(c, 'Contact')} <SupportEmail />{' '}
          {tt(
            c,
            "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
          )}
        </>
      );
    }
  }
} satisfies Partial<CopyPack>;
